/**
 * Sensor system demo, in the investor area. See `locales/es/demo.ts`.
 *
 * Same rule applies: **nothing written here may claim that anything is
 * deployed, in production or measuring a real event.** Describing what the
 * product does is fine; presenting it as a measurement in progress is not.
 */

export const demo = {
  eyebrow: 'Product demo',
  titulo: 'What a brand sees at its booth',
  subtitulo:
    'Out of every hundred people who walk into a booth, a handful tap their badge. Everyone sells that handful. This shows the rest as well: who stayed, how long, with what expression — and how to recover the ones who left without leaving contact details.',
  // El aviso arma la frase como `<sujeto> is demo data`, asi que el sujeto
  // tiene que ser singular. En plural salia "The people ... is demo data".
  avisoDatos: 'Everything on this screen — the people, the figures and the paths —',

  seccionesLabel: 'Parts of the panel',
  secciones: {
    camara: 'Booth camera',
    activos: 'Active leads',
    pasivos: 'Passive leads',
    radar: 'mmWave radar',
  },

  /** Fair clock, shared by the camera and the two lead tables. See `locales/es/demo.ts`. */
  reloj: {
    reproduciendo: 'Replaying the day',
    jornadaCerrada: 'Day closed',
    hora: (h: string, m: string) => `${h}:${m}`,

    segundos: (s: string) => `:${s}`,

    velocidad: 'Speed',
    velocidadLabel: 'Playback speed',
    multiplo: (n: string) => `${n}×`,
    jornadaEn: (duracion: string) => `The whole day in ${duracion}`,
    duracionSegundos: (s: string) => `${s} s`,
    duracionMinutos: (m: string) => `${m} min`,
    duracionHoras: (h: string) => `${h} h`,
  },

  /** Front door of the open demo: a sign-in that authenticates nothing. */
  aviso: {
    antes: (sujeto: string) => `${sujeto} is `,
    fuerte: 'demo data',
    despues: ': it shows what the screen will look like, and does not describe a real event.',
  },

  acceso: {
    correo: 'Email',
    contrasena: 'Password',
    titulo: 'Brand panel',
    subtitulo:
      'This is how a brand signs in to its booth panel. What follows is a simulation with invented data: no account, no session, nothing is sent to any server.',
    aviso:
      'Demo: this sign-in always opens. The credentials come filled in and are checked against nothing.',
    credenciales: 'Sample credentials, fixed and inert.',
    entrar: 'Open the panel',
    volverAlSitio: 'Back to the site',
  },

  cierre:
    'The camera and the radar measure the same thing in different ways and are never added together: one person would be counted twice. That is why the system keeps the counts apart and makes you pick one source.',
  salir: 'Leave the demo',

  camara: {
    mirandoAhora: 'Looking right now',
    afluencia: 'Came into the stand',
    atencionMedia: 'Average attention',
    enganche: 'Engagement index',

    expresion: 'How they looked',
    expresionDetalle:
      'Expression mix of everyone in front of the stand, weighted by how long they stayed. Six minutes count as six minutes; forty seconds count as forty seconds.',
    repartoAlt: (dominante: string) => `Expression mix. Predominant: ${dominante}.`,

    curva: 'Engagement through the day',
    curvaDetalle:
      'How much of the attention was interest or surprise, minute by minute. It tells you when it pays to have people on the stand, and when nothing happens.',
    curvaAlt: (pct: number) => `Engagement curve for the day. Right now, ${pct} percent.`,

    emociones: {
      interes: 'Interest',
      sorpresa: 'Surprise',
      duda: 'Doubt',
      neutral: 'Neutral',
      desinteres: 'Disinterest',
    },

    privacidad:
      'The camera stores no images and recognizes no faces. What comes out of it is aggregate: how many looked, how long they held it and with what expression, plus an estimated age bracket. Never an age, never a person.',

    segundos: (s: string) => `${s} s`,
  },

  leads: {
    permanenciaMedia: 'Average dwell',
    recurrentes: 'Came back',
    perfil: 'What they have in common',
    perfilDetalle:
      'The profile of the group, not of each person. It is what you use to decide who to bring to the stand tomorrow.',
    segundos: (s: string) => `${s} s`,

    columnas: {
      persona: 'Person',
      empresa: 'Company',
      cargo: 'Role',
      edad: 'Estimated age',
      areas: 'Where they stopped',
      permanencia: 'Dwell',
      expresion: 'Expression',
    },

    cargos: {
      direccion: 'Executive',
      gerencia: 'Management',
      especialista: 'Specialist',
      otro: 'Other',
    },

    areas: {
      pantalla: 'Screen',
      mesa: 'Table',
      producto: 'Product',
      mostrador: 'Counter',
    },

    estados: {
      anonimo: 'Not identified',
      rechazo: 'Declined to share',
      sin_respuesta: 'No answer',
    },

    activos: {
      total: 'Active leads',
      conversion: 'Of everyone who came in',
      tabla: 'Latest captured leads',
      explica:
        'They tapped their badge on the stand reader. They gave consent deliberately, so you have their full contact details. This is what everyone already sells.',
    },

    pasivos: {
      total: 'Passive leads',
      perdidos: 'Of everyone who came in',
      tabla: 'Who was there and left no contact',
      explica: (umbral: number) =>
        `They stayed longer than ${umbral} seconds and never tapped their badge. Today, as far as your brand is concerned, these people simply did not happen. The camera did see them, and the event registry knows who they are — but that is Ilumetria's knowledge, not yours.`,
    },

    campana: {
      titulo: 'Recover these contacts',
      explica:
        'We write to them ourselves, in the name of the event, and ask whether they want to share their contact details with you. Whoever accepts joins your list; whoever does not stays a row in this table, and you will never know who they were.',
      soloAceptados: 'You pay per recovered contact, not per message sent.',
      lanzar: (cuantos: number) => `Ask all ${cuantos}`,
      consentimiento:
        "Nobody gets your data and you get nobody's until the person says yes. This is consent asked after the visit, not a purchased list.",
      enviando: (van: number, total: number) => `${van} of ${total} have replied…`,
      resuelta: (aceptaron: number, total: number) =>
        `${aceptaron} of ${total} agreed to share their contact. They are on your list now.`,
      aceptaron: 'Accepted',
      rechazaron: 'Declined',
      callaron: 'No answer',
    },
  },

  radar: {
    aforo: 'People inside now',
    visitas: 'Visits today',
    entradas: 'Entries',
    salidas: 'Exits',

    enVivo: 'Walkthrough running · 10 frames per second',
    enVivoRapido: (multiplo: string) => `Walkthrough running · played back at ${multiplo}×`,
    notaVelocidad:
      'The radar streams at ten frames per second and this is a replay: the speed above moves the floor plan just as it moves the clock. What it does not move are the figures — dwell and median are in real seconds of the walkthrough, and watching it faster does not shorten what it took.',
    sinAnimacion: 'Animation turned off by your system preferences',
    mapaDeCalor: 'Heat map',

    porZona: 'People per zone',
    notaZonas:
      'The radar reports coordinates in meters, so zones are drawn straight onto the booth plan and dwell time per zone falls out of it. There are no images and no biometrics: it is anonymous by physics.',

    lienzoAlt: (aforo: number) =>
      `Top-down view of the booth with visitor paths. Right now there are ${aforo} people inside.`,
    personasEnZona: (cuantas: number, zona: string) => `${cuantas} people in ${zona}`,

    /** Zone labels. The geometry lives in `data/escenario.ts`. */
    zonas: {
      'stand-1': 'Booth 1',
      'stand-2': 'Booth 2',
      'stand-3': 'Booth 3',
    },

    /* ── Closed-visit metrics ── */

    analitica: 'What each visit leaves behind',
    analiticaDetalle:
      'The live view says how many people are there; this says what each one did. These are the same metrics the radar emits when it closes a track.',
    permanenciaMedia: 'Average dwell',
    permanenciaMediana: 'Median',
    notaMediana:
      'The average and the median do not match, and that gap is the point: people crossing the aisle without looking drag the average down without touching the median. A panel showing only one of them would suggest nobody stops.',
    distanciaMedia: 'Average distance walked',
    calidad: 'Fragments per visit',
    notaCalidad:
      'How many separate stretches had to be stitched together to rebuild each path. One is perfect; above two, the radar’s own spec considers dwell no longer credible. We publish it because a measurement system that hides its own margin of error cannot be audited.',
    permanenciaPorZona: 'Dwell per booth',
    visitantesZona: (n: number) => `${n} visitors`,
    notaSolapan:
      'The zones overlap on purpose, so dwell per booth can add up to more than total dwell: a second inside booth 2 is also a second inside the measured area.',
    // Reciben la cifra **ya formateada** por `useFormato`: el separador
    // decimal cambia con el idioma, así que formatearla aquí sería
    // reimplementar `Intl` en el diccionario.
    segundos: (s: string) => `${s} s`,
    metros: (m: string) => `${m} m`,
  },
};
